// US-035–040: File sharing panel with upload, progress, history, unread

import { useRef, useState } from "react"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"
import type { SharedFile } from "@vc/models"
import { formatFileSize, formatTimestamp } from "@vc/services/format"
import { Button } from "@/clientWeb/components/ui/button"
import { File } from "lucide-react"

export function FilesPanel({
  files,
  localUserId,
  canShare = true,
  canClear = false,
  onShare,
  onMarkRead,
  onClear,
}: {
  files: SharedFile[]
  localUserId: string
  canShare?: boolean
  canClear?: boolean
  onShare?: (file: File) => void
  onMarkRead?: (id: string) => void
  onClear?: () => void
}) {
  const t = useTranslation();
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [dragging, setDragging] = useState(false)

  // -----------------------------
  // Dummy files (shown initially)
  // -----------------------------
  const dummyFiles: SharedFile[] = [
    {
      id: "1",
      name: "Project-Requirements.pdf",
      size: 245760,
      senderId: "u-john",
      senderName: "John",
      timestamp: Date.now() - 1000 * 60 * 20,
      status: "ready",
      url: "#",
      progress: 100,
      type: "active",
      isRead: false,
    },
    {
      id: "2",
      name: "Design-Mockup.fig",
      size: 534000,
      senderId: "u-alice",
      senderName: "Alice",
      timestamp: Date.now() - 1000 * 60 * 60,
      status: "ready",
      url: "#",
      progress: 100,
      type: "active",
      isRead: true,
    },
    {
      id: "3",
      name: "MeetingRecording.mp4",
      size: 8500000,
      senderId: localUserId,
      senderName: localUserId,
      timestamp: Date.now() - 1000 * 60 * 120,
      status: "uploading",
      progress: 60,
      type: "active",
      isRead: false,
    },
  ]

  // If no real files exist → show dummy
  const displayFiles = files.length ? files : dummyFiles

  const handleFiles = (fileList: FileList | null) => {
    if (!fileList || !onShare) return
    Array.from(fileList).forEach((f) => onShare(f))
  }

  return (
    <div className="flex h-full flex-col">

      {/* Header */}
      <div className="flex items-center justify-between border-b border-stroke-100 dark:border-border px-4 py-3">
        <div className="text-sm font-semibold">
          {t("panels.sharedFiles")} <span className="ml-1">({displayFiles.length})</span>
        </div>

        {canClear && onClear && (
          <Button
            variant="ghost"
            onClick={onClear}
          >
            {t("panels.clearAll")}
          </Button>
        )}
      </div>


      {/* Drop zone */}
      {canShare && (
        <div
          className={[
            "mx-4 mt-3 cursor-pointer rounded-2xl border-2 border-dashed p-5 text-center transition",
            dragging
              ? "border-stroke-100 dark:border-border bg-primary/10"
              : "border-stroke-100 dark:border-border",
          ].join(" ")}
          onDragOver={(e) => {
            e.preventDefault()
            setDragging(true)
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault()
            setDragging(false)
            handleFiles(e.dataTransfer.files)
          }}
          onClick={() => inputRef.current?.click()}
        >

          <svg
            className="mx-auto h-7 w-7 text-black dark:text-foreground/30"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
            />
          </svg>

          <div className="mt-2 text-xs">
            {t("panels.dropFilesHereOr")} <span className="text-primary">{t("panels.browse")}</span>
          </div>

          <input
            ref={inputRef}
            type="file"
            multiple
            className="hidden"
            onChange={(e) => handleFiles(e.target.files)}
          />
        </div>
      )}


      {/* File list */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2">

        {displayFiles.map((f) => (
          <div
            key={f.id}
            className={[
              "group flex items-start gap-3 rounded-2xl p-3 ring-1 transition",
              !f.isRead
                ? "bg-primary-100/20 ring-stroke-200"
                : "bg-black/5 ring-black/10",
            ].join(" ")}
            onClick={() => onMarkRead?.(f.id)}
          >

            {/* File icon */}
            <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-card/10 ring-1 ring-white/10">
              <File />
            </div>


            {/* File info */}
            <div className="min-w-0 flex-1">

              <div className="flex items-start justify-between gap-2">
                <div className="truncate text-sm font-medium">
                  {f.name}
                </div>

                {!f.isRead && (
                  <div className="h-2 w-2 shrink-0 rounded-full bg-primary mt-1" />
                )}
              </div>


              <div className="mt-0.5 text-[11px]">
                {f.senderName === localUserId
                  ? t("panels.you")
                  : f.senderName} · {formatFileSize(f.size)} · {formatTimestamp(f.timestamp)}
              </div>


              {/* Upload progress */}
              {f.status === "uploading" && (
                <div className="mt-2">

                  <div className="h-1 w-full overflow-hidden rounded-full bg-black/10">
                    <div
                      className="h-full rounded-full bg-primary transition-all"
                      style={{ width: `${f.progress}%` }}
                    />
                  </div>

                  <div className="mt-1 text-[10px] text-black dark:text-muted-foreground">
                    {f.progress}%
                  </div>

                </div>
              )}


              {/* Download */}
              {f.status === "ready" && f.url && (
                <a
                  href={f.url}
                  download={f.name}
                  onClick={(e) => e.stopPropagation()}
                  className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-semibold transition"
                >

                  <svg
                    className="h-3 w-3"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                    />
                  </svg>

                  {t("panels.download")}
                </a>
              )}

            </div>
          </div>
        ))}

      </div>
    </div>
  )
}